// Helyszínek tömeges importja CSV-ből (pl. a geokódolt dart-klub lista).
// Elfogadott oszlopok (fejléc alapján, kis/nagybetű és ékezet nem számít):
//   név: „nev” / „Klub / hely hivatalos neve”   cím: „cim” / „Teljes cím” („1191 Budapest, Utca 1.”)
//   lat / lng (opcionális), pontossag (opcionális – „csak település” sor GPS nélkül kerül be)
// Párosítás a meglévőkkel: név, vagy város + cím egyezése. Meglévőnél csak a hiányzó GPS-t és címet pótoljuk.
import React, { useMemo, useState } from 'react';
import { addDoc, collection, doc, updateDoc, type DocumentData } from 'firebase/firestore';
import { db } from '../../firebase';
import { Badge, Button, Modal, useToast } from '../ui';

export interface ExistingVenue { id: string; name: string; city: string; address: string; lat: number | null; lng: number | null }

type Action = 'new' | 'update' | 'skip';
interface Row {
  name: string; city: string; postalCode: string; address: string;
  lat: number | null; lng: number | null; precision: string;
  action: Action; matchId: string | null; note: string; checked: boolean;
}

const norm = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, ' ').trim();

/** Egyszerű CSV-olvasó (idézőjelek, vessző vagy pontosvessző elválasztó, BOM). */
function parseCsv(text: string): string[][] {
  const t = text.replace(/^﻿/, '');
  const firstLine = t.split(/\r?\n/, 1)[0] ?? '';
  const sep = (firstLine.match(/;/g)?.length ?? 0) > (firstLine.match(/,/g)?.length ?? 0) ? ';' : ',';
  const rows: string[][] = []; let row: string[] = []; let cell = ''; let q = false;
  for (let i = 0; i < t.length; i++) {
    const ch = t[i];
    if (q) {
      if (ch === '"' && t[i + 1] === '"') { cell += '"'; i++; } else if (ch === '"') q = false; else cell += ch;
    } else if (ch === '"') q = true;
    else if (ch === sep) { row.push(cell); cell = ''; }
    else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && t[i + 1] === '\n') i++;
      row.push(cell); cell = ''; if (row.some((c) => c.trim())) rows.push(row); row = [];
    } else cell += ch;
  }
  row.push(cell); if (row.some((c) => c.trim())) rows.push(row);
  return rows;
}

/** „1191 Budapest, Vak Bottyán utca 75/A-C” → irányítószám, város, cím. */
function splitAddress(full: string): { postalCode: string; city: string; address: string } {
  const m = full.trim().match(/^(\d{4})\s+([^,]+),\s*(.+)$/);
  if (m) return { postalCode: m[1], city: m[2].trim(), address: m[3].trim() };
  const [a, ...rest] = full.split(',');
  return rest.length ? { postalCode: '', city: a.trim(), address: rest.join(',').trim() } : { postalCode: '', city: '', address: full.trim() };
}

function buildRows(csv: string[][], existing: ExistingVenue[]): Row[] {
  const [head, ...body] = csv;
  const h = head.map(norm);
  const col = (...keys: string[]) => h.findIndex((x) => keys.some((k) => x === k || x.includes(k)));
  const iName = col('nev', 'neve', 'name');
  const iAddr = col('cim', 'address');
  const iLat = col('lat', 'szelesseg');
  const iLng = col('lng', 'lon', 'hosszusag');
  const iPrec = col('pontossag', 'precision');
  if (iName < 0 || iAddr < 0) throw new Error('Nem találom a név és a cím oszlopot (pl. „nev” és „cim”).');
  const byName = new Map(existing.map((v) => [norm(v.name), v]));
  const byAddr = new Map(existing.map((v) => [norm(`${v.city} ${v.address}`), v]));
  const seen = new Set<string>();
  return body.map((r) => {
    const name = (r[iName] ?? '').trim();
    const { postalCode, city, address } = splitAddress(r[iAddr] ?? '');
    const precision = iPrec >= 0 ? (r[iPrec] ?? '').trim() : '';
    let lat = iLat >= 0 ? Number(String(r[iLat] ?? '').replace(',', '.')) : NaN;
    let lng = iLng >= 0 ? Number(String(r[iLng] ?? '').replace(',', '.')) : NaN;
    const vague = /telepules|nem talalhato/.test(norm(precision));
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || !lat || !lng || vague) { lat = NaN; lng = NaN; }
    const hasGps = Number.isFinite(lat);
    const key = norm(name);
    const match = byName.get(key) ?? byAddr.get(norm(`${city} ${address}`)) ?? null;
    let action: Action = 'new'; let note = hasGps ? '' : 'GPS nélkül kerül be';
    if (!name || !city) { action = 'skip'; note = 'Hiányzó név vagy város'; }
    else if (seen.has(key)) { action = 'skip'; note = 'Ismétlődő sor a fájlban'; }
    else if (match) {
      const needGps = (match.lat === null || match.lng === null) && hasGps;
      const needAddr = !match.address && !!address;
      action = needGps || needAddr ? 'update' : 'skip';
      note = action === 'update' ? `Meglévő: ${[needGps && 'GPS', needAddr && 'cím'].filter(Boolean).join(' és ')} pótlása` : 'Már megvan, nincs mit pótolni';
    }
    seen.add(key);
    return {
      name, city, postalCode, address, lat: hasGps ? lat : null, lng: hasGps ? lng : null, precision,
      action, matchId: match?.id ?? null, note, checked: action !== 'skip',
    };
  });
}

export function VenueImportModal({ existing, onClose, onDone }: { existing: ExistingVenue[]; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  const onFile = async (f: File | undefined) => {
    setMsg(''); setRows(null);
    if (!f) return;
    try {
      const csv = parseCsv(await f.text());
      if (csv.length < 2) throw new Error('A fájl üres.');
      setRows(buildRows(csv, existing));
    } catch (e) { setMsg(e instanceof Error ? e.message : 'A fájl nem olvasható.'); }
  };

  const stats = useMemo(() => {
    const sel = (rows ?? []).filter((r) => r.checked);
    return { add: sel.filter((r) => r.action === 'new').length, upd: sel.filter((r) => r.action === 'update').length, noGps: sel.filter((r) => r.lat === null).length };
  }, [rows]);

  const run = async () => {
    if (!rows) return;
    const todo = rows.filter((r) => r.checked && r.action !== 'skip');
    setBusy(true); setProgress(0);
    let ok = 0; let fail = 0;
    for (const r of todo) {
      try {
        if (r.action === 'new') {
          await addDoc(collection(db, 'venues'), {
            name: r.name, city: r.city, address: r.address, postalCode: r.postalCode,
            latitude: r.lat, longitude: r.lng, pricePerHour: 0,
            operatorId: '', isActive: true, status: 'active', source: 'import', createdAt: new Date().toISOString(),
          });
        } else if (r.matchId) {
          const ex = existing.find((v) => v.id === r.matchId);
          const patch: DocumentData = {};
          if (ex && (ex.lat === null || ex.lng === null) && r.lat !== null) { patch.latitude = r.lat; patch.longitude = r.lng; }
          if (ex && !ex.address && r.address) { patch.address = r.address; patch.postalCode = r.postalCode; }
          if (Object.keys(patch).length) await updateDoc(doc(db, 'venues', r.matchId), patch);
        }
        ok++;
      } catch (e) { console.warn('[venues] import', r.name, e); fail++; }
      setProgress((p) => p + 1);
    }
    setBusy(false);
    toast(fail ? `Import kész: ${ok} sikeres, ${fail} sikertelen.` : `Import kész: ${ok} helyszín.`, fail ? 'red' : undefined);
    onDone(); onClose();
  };

  const toggle = (i: number) => setRows((rs) => rs && rs.map((r, j) => (j === i && r.action !== 'skip' ? { ...r, checked: !r.checked } : r)));
  const todoCount = (rows ?? []).filter((r) => r.checked && r.action !== 'skip').length;

  return (
    <Modal title="Helyszínek importálása CSV-ből" onClose={busy ? () => undefined : onClose} width={980}
      footer={(
        <div className="flex items-center justify-between w-full gap-3">
          <span className="text-[13px] text-[#8a8f99]">
            {rows ? `${stats.add} új · ${stats.upd} frissítés · ${stats.noGps} GPS nélkül` : 'Válaszd ki a CSV-fájlt.'}
            {busy && ` · ${progress}/${todoCount}`}
          </span>
          <div className="flex gap-2">
            <Button kind="ghost" onClick={onClose} disabled={busy}>Mégse</Button>
            <Button icon="UploadSimple" onClick={run} disabled={!rows || !todoCount || busy}>{busy ? 'Importálás…' : `Importálás (${todoCount})`}</Button>
          </div>
        </div>
      )}>
      <div className="space-y-4">
        <p className="text-[13px] text-[#B8B8C3]">
          Oszlopok: <b className="text-white">nev</b>, <b className="text-white">cim</b> („1191 Budapest, Utca 1.”), opcionálisan <b className="text-white">lat</b>, <b className="text-white">lng</b>, <b className="text-white">pontossag</b>.
          A meglévő helyszíneknél csak a hiányzó GPS és cím pótlódik, semmi nem íródik felül. A „csak település” pontosságú sorok GPS nélkül kerülnek be.
        </p>
        <input type="file" accept=".csv,text/csv" disabled={busy} onChange={(e) => onFile(e.target.files?.[0])}
          className="block text-[13px] text-[#B8B8C3] file:mr-3 file:h-[36px] file:px-4 file:rounded-[10px] file:border-0 file:bg-[#1d232a] file:text-white" />
        {msg && <p className="text-[13px] text-[#ef4444]">{msg}</p>}
        {rows && (
          <div className="max-h-[420px] overflow-auto border border-[#31383D] rounded-xl">
            <table className="w-full text-left text-[13px]">
              <thead className="sticky top-0 bg-[#1d232a] text-[11.5px] uppercase text-[#8a8f99]">
                <tr><th className="pl-3 w-8" /><th className="px-3 h-[34px]">Név</th><th className="px-3">Város, cím</th><th className="px-3">GPS</th><th className="px-3">Művelet</th></tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className={`border-t border-[#31383D] ${r.checked ? '' : 'opacity-50'}`}>
                    <td className="pl-3"><input type="checkbox" checked={r.checked} disabled={r.action === 'skip' || busy} onChange={() => toggle(i)} /></td>
                    <td className="px-3 py-2 text-white font-semibold">{r.name || '—'}</td>
                    <td className="px-3 text-[#B8B8C3]">{[r.city, r.address].filter(Boolean).join(', ')}</td>
                    <td className="px-3">{r.lat !== null ? <Badge tone="green">GPS ✓</Badge> : <Badge tone="gray">Nincs</Badge>}</td>
                    <td className="px-3">
                      <Badge tone={r.action === 'new' ? 'cyan' : r.action === 'update' ? 'gold' : 'gray'}>{r.action === 'new' ? 'Új' : r.action === 'update' ? 'Frissítés' : 'Kihagyás'}</Badge>
                      {r.note && <span className="ml-2 text-[12px] text-[#8a8f99]">{r.note}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
}
